import { useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { ArrowUpIcon } from '@solar-icons/react/linear/arrow-up'
import { MicrophoneIcon } from '@solar-icons/react/bold/microphone'
import { PaperclipIcon } from '@solar-icons/react/linear/paperclip'
import { StopIcon } from '@solar-icons/react/bold/stop'
import { DatabaseIcon } from '@solar-icons/react/linear/database'
import { Button, Dropdown } from '@/components/ui'
import { cn } from '@/lib/utils/cn'
import { useAutosizeTextarea } from '@/lib/hooks/useAutosizeTextarea'
import { useSpeechRecognition } from '@/lib/hooks/useSpeechRecognition'
import { useSettingsStore } from '@/stores/settingsStore'
import { QUERY_ROUTE_LABELS } from '@/lib/api/queryApi'
import type { QueryRoute } from '@/lib/api/queryApi'

export type ComposerVariant = 'dock' | 'hero'

export interface ComposerProps {
  onSubmit: (query: string) => void
  onCancel: () => void
  isRunning: boolean
  disabled?: boolean
  /** Set by the suggestion rail / history / saved views to prefill the input. */
  value: string
  onChange: (value: string) => void
  /**
   * `dock` — pinned to the bottom of an active thread, own top border.
   * `hero` — embedded in the landing screen, larger and border-free so it
   * reads as part of the page rather than a docked toolbar.
   */
  variant?: ComposerVariant
  className?: string
}

const SOURCE_LABEL: Record<QueryRoute, string> = { test: 'Test data', production: 'Production data' }

export function Composer({
  onSubmit,
  onCancel,
  isRunning,
  disabled,
  value,
  onChange,
  variant = 'dock',
  className,
}: ComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [voiceNotice, setVoiceNotice] = useState<string | null>(null)

  const route = useSettingsStore((state) => state.route)
  const setRoute = useSettingsStore((state) => state.setRoute)

  useAutosizeTextarea(textareaRef, value)

  const speech = useSpeechRecognition({
    onTranscript: (transcript) => onChange(transcript),
    onFinal: () => {
      setVoiceNotice(null)
      textareaRef.current?.focus()
    },
  })

  const submit = () => {
    const query = value.trim()
    if (!query || isRunning || disabled) return
    onSubmit(query)
    onChange('')
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submit()
    }
    if (event.key === 'Escape' && isRunning) {
      event.preventDefault()
      onCancel()
    }
  }

  const handleVoiceClick = () => {
    if (!speech.isSupported) {
      setVoiceNotice('Voice input needs a Chromium-based browser.')
      return
    }
    setVoiceNotice(null)
    speech.toggle()
  }

  const notice = voiceNotice ?? speech.error
  const isHero = variant === 'hero'

  return (
    <div className={cn(isHero ? '' : 'px-4 pt-2 pb-4', className)}>
      <div className={cn('mx-auto w-full', isHero ? 'max-w-2xl' : 'max-w-6xl')}>
        <div
          className={cn(
            'rounded-[26px] border bg-surface transition-colors',
            isHero ? 'px-4 pt-3.5 pb-2.5 shadow-lg' : 'px-4 pt-3 pb-2 shadow-sm',
            'focus-within:border-line-strong',
            speech.isListening ? 'border-accent' : 'border-line',
          )}
        >
          <textarea
            ref={textareaRef}
            rows={1}
            value={value}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              speech.isListening
                ? 'Listening…'
                : isHero
                  ? 'Message QAP…'
                  : 'Ask me anything…'
            }
            aria-label="Query"
            className={cn(
              'scrollbar-thin block max-h-50 w-full resize-none bg-transparent',
              'text-sm leading-relaxed text-fg outline-none placeholder:text-subtle',
              'disabled:cursor-not-allowed disabled:opacity-60',
              'min-h-6',
            )}
          />

          <div className="mt-2 flex items-center gap-2">
            <Dropdown
              value={route}
              onChange={setRoute}
              disabled={disabled}
              ariaLabel="Select data source"
              placeholder="Select source"
              icon={<DatabaseIcon size={13} strokeWidth={1.75} className="text-subtle" />}
              options={[
                { value: 'test', label: SOURCE_LABEL.test, description: QUERY_ROUTE_LABELS.test },
                {
                  value: 'production',
                  label: SOURCE_LABEL.production,
                  description: QUERY_ROUTE_LABELS.production,
                },
              ]}
            />

            <div className="ml-auto flex shrink-0 items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                disabled
                aria-label="Attach a file"
                title="Attachments aren't supported yet"
              >
                <PaperclipIcon size={16} strokeWidth={1.75} />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleVoiceClick}
                aria-label={speech.isListening ? 'Stop listening' : 'Start voice input'}
                title={
                  speech.isSupported
                    ? speech.isListening
                      ? 'Stop listening'
                      : 'Speak your query'
                    : 'Voice input is unavailable in this browser'
                }
                className={cn(
                  speech.isListening && 'bg-accent-soft text-accent',
                  !speech.isSupported && 'opacity-40',
                )}
              >
                <MicrophoneIcon size={16} />
              </Button>

              {isRunning ? (
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={onCancel}
                  title="Cancel the running query (Esc)"
                  aria-label="Stop"
                >
                  <StopIcon size={13} />
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="icon"
                  onClick={submit}
                  disabled={!value.trim() || disabled}
                  aria-label="Send"
                  title="Send (Enter)"
                >
                  <ArrowUpIcon size={16} strokeWidth={2.25} />
                </Button>
              )}
            </div>
          </div>
        </div>

        {notice && (
          <div className="mt-2 flex items-center justify-center gap-3 px-1 text-center">
            <p className="text-[11px] text-warning" role="status">
              {notice}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
